import { ID_CARD_DPI, ID_CARD_PORTRAIT, ID_CARD_LANDSCAPE } from './idCardTheme';

export const SHEET_SIZES = {
  a4: { widthMm: 210, heightMm: 297, label: 'A4' },
  letter: { widthMm: 215.9, heightMm: 279.4, label: 'Letter' },
  legal: { widthMm: 215.9, heightMm: 355.6, label: 'Legal' },
};

export const DEFAULT_PRINT_SETTINGS = {
  sheetSize: 'a4',
  sheetOrientation: 'portrait',
  cardWidthIn: ID_CARD_PORTRAIT.widthIn,
  cardHeightIn: ID_CARD_PORTRAIT.heightIn,
  cardFormat: 'portrait',
  cardsPerRow: null,
  cardsPerColumn: null,
  autoFit: true,
  marginMm: 8,
  gapHorizontalMm: 3,
  gapVerticalMm: 3,
  showCropMarks: true,
  showPageBorder: false,
  includeBack: true,
};

const NUMERIC_KEYS = [
  'cardWidthIn',
  'cardHeightIn',
  'cardsPerRow',
  'cardsPerColumn',
  'marginMm',
  'gapHorizontalMm',
  'gapVerticalMm',
];

const BOOL_KEYS = ['autoFit', 'showCropMarks', 'showPageBorder', 'includeBack'];

const STRING_KEYS = ['sheetSize', 'sheetOrientation', 'cardFormat'];

function clampNum(val, min, max, fallback) {
  const n = Number(val);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/**
 * Normalize and merge saved/API settings with defaults.
 */
export function mergePrintSettings(saved = {}, overrides = {}) {
  const merged = { ...DEFAULT_PRINT_SETTINGS, ...saved, ...overrides };

  merged.sheetSize = SHEET_SIZES[merged.sheetSize] ? merged.sheetSize : 'a4';
  merged.sheetOrientation = merged.sheetOrientation === 'landscape' ? 'landscape' : 'portrait';
  merged.cardFormat = merged.cardFormat === 'landscape' ? 'landscape' : 'portrait';

  merged.cardWidthIn = clampNum(merged.cardWidthIn, 1, 4, DEFAULT_PRINT_SETTINGS.cardWidthIn);
  merged.cardHeightIn = clampNum(merged.cardHeightIn, 1.5, 6, DEFAULT_PRINT_SETTINGS.cardHeightIn);
  merged.marginMm = clampNum(merged.marginMm, 0, 40, DEFAULT_PRINT_SETTINGS.marginMm);
  merged.gapHorizontalMm = clampNum(merged.gapHorizontalMm, 0, 30, DEFAULT_PRINT_SETTINGS.gapHorizontalMm);
  merged.gapVerticalMm = clampNum(merged.gapVerticalMm, 0, 30, DEFAULT_PRINT_SETTINGS.gapVerticalMm);

  merged.autoFit = merged.autoFit !== false;
  merged.showCropMarks = merged.showCropMarks !== false;
  merged.showPageBorder = merged.showPageBorder === true;
  merged.includeBack = merged.includeBack !== false;

  if (!merged.autoFit) {
    merged.cardsPerRow = clampNum(merged.cardsPerRow, 1, 20, 2);
    merged.cardsPerColumn = clampNum(merged.cardsPerColumn, 1, 20, 2);
  } else {
    merged.cardsPerRow = null;
    merged.cardsPerColumn = null;
  }

  return merged;
}

/**
 * Serialize settings for API persistence (omit null auto-fit fields).
 */
export function serializePrintSettings(settings) {
  const s = mergePrintSettings(settings);
  const out = {};
  STRING_KEYS.forEach((k) => { out[k] = s[k]; });
  NUMERIC_KEYS.forEach((k) => {
    if (s[k] != null) out[k] = s[k];
  });
  BOOL_KEYS.forEach((k) => { out[k] = s[k]; });
  return out;
}

export function cardSizeFromSettings(settings) {
  const s = mergePrintSettings(settings);
  const base = s.cardFormat === 'landscape' ? ID_CARD_LANDSCAPE : ID_CARD_PORTRAIT;
  return {
    widthIn: s.cardWidthIn,
    heightIn: s.cardHeightIn,
    widthPx: Math.round(s.cardWidthIn * ID_CARD_DPI),
    heightPx: Math.round(s.cardHeightIn * ID_CARD_DPI),
    baseWidthPx: base.widthPx,
    baseHeightPx: base.heightPx,
    label: `${s.cardWidthIn}" × ${s.cardHeightIn}"`,
  };
}

export function getPageDimensionsMm(settings) {
  const s = mergePrintSettings(settings);
  const preset = SHEET_SIZES[s.sheetSize] || SHEET_SIZES.a4;
  if (s.sheetOrientation === 'landscape') {
    return { pageWidthMm: preset.heightMm, pageHeightMm: preset.widthMm };
  }
  return { pageWidthMm: preset.widthMm, pageHeightMm: preset.heightMm };
}
